import { Check, X } from "lucide-react";
import { ScoreDimensions } from "./ScoreDimensions";

const VERDICT_TONE = {
  Trivial: "sev-critical",
  Easy: "sev-high",
  Moderate: "sev-medium",
  Hard: "sev-low",
  Theoretical: "sev-default",
};

export default function ExploitDifficulty({ ex }) {
  if (!ex) return null;
  const score = Number(ex.score ?? 0);
  const pct = Math.max(0, Math.min(100, score * 10));
  const verdict = ex.verdict || "Moderate";
  const tone = VERDICT_TONE[verdict] || "sev-default";

  return (
    <section
      data-testid="exploit-difficulty"
      className="panel p-6 md:p-7 fade-up delay-1 relative overflow-hidden"
    >
      <div className="relative">
        <div className="overline">Technical exploitability</div>
        <h3 className="font-display text-xl text-slate-900 mt-1 mb-5">
          How hard is it to actually pull off?
        </h3>

        <div className="flex items-end justify-between gap-4 mb-3">
          <div>
            <div
              data-testid="exploit-score"
              className="font-mono font-bold text-5xl md:text-6xl text-slate-900 tracking-tighter"
            >
              {score.toFixed(1)}
              <span className="text-slate-500 text-2xl">/10</span>
            </div>
            <div
              data-testid="exploit-verdict"
              className={`inline-block mt-2 border ${tone} rounded-md px-2.5 py-1 text-xs font-bold uppercase tracking-wider`}
            >
              {verdict}
            </div>
          </div>
          <div className="text-right text-xs text-slate-600">
            <div className="overline">Attacker skill</div>
            <div className="text-slate-900 font-mono mt-1">
              {ex.attacker_skill || "-"}
            </div>
            <div className="overline mt-3">Time to exploit</div>
            <div className="text-slate-900 font-mono mt-1">
              {ex.time_to_exploit || "-"}
            </div>
          </div>
        </div>

        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-5">
          <div
            className="h-full"
            style={{
              width: `${pct}%`,
              background:
                pct >= 85 ? "#dc2626" : pct >= 70 ? "#ea580c" : pct >= 45 ? "#ca8a04" : "#16a34a",
            }}
          />
        </div>

        <ScoreDimensions dims={ex.dimensions} legacyScore={ex.legacy_score} />

        {ex.reasoning && (
          <p className="text-slate-700 text-sm leading-relaxed mb-5">
            {ex.reasoning}
          </p>
        )}

        {Array.isArray(ex.prerequisites) && ex.prerequisites.length > 0 && (
          <div>
            <div className="overline mb-2">Prerequisites</div>
            <ul className="space-y-1.5" data-testid="prerequisites">
              {ex.prerequisites.map((p, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 text-sm text-slate-700"
                >
                  {p.required ? (
                    <span className="h-4 w-4 rounded-sm bg-red-50 border border-red-200 grid place-items-center">
                      <Check className="h-2.5 w-2.5 text-red-700" />
                    </span>
                  ) : (
                    <span className="h-4 w-4 rounded-sm bg-slate-100 border border-slate-200 grid place-items-center">
                      <X className="h-2.5 w-2.5 text-slate-500" />
                    </span>
                  )}
                  <span className={p.required ? "text-slate-900" : "text-slate-500"}>
                    {p.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {Array.isArray(ex.drivers) && ex.drivers.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-100" data-testid="exploit-drivers">
            <div className="overline mb-2 flex items-center gap-2">
              <span>Score drivers</span>
              {ex.deterministic && (
                <span className="text-[9px] font-mono uppercase tracking-wider text-violet-700 border border-violet-200 bg-violet-50 px-1.5 py-0.5 rounded-sm">
                  deterministic
                </span>
              )}
            </div>
            <ul className="space-y-1 text-[11px] font-mono text-slate-600 leading-relaxed">
              {ex.drivers.map((d, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-violet-700 shrink-0">{">"}</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
